/**
 * AI Configuration - Usage Analytics Tab
 *
 * T017: Cost, API Requests, Accepted Rate, Correct Rate, Time Saved,
 * Recent Activity, Performance Summary. Empty state: "No AI activity yet".
 */

import React from 'react';
import { Loader2, DollarSign, Zap, TrendingUp, Clock, Activity } from 'lucide-react';
import { useUsageAnalytics } from '../../../hooks/useUsageAnalytics';
import { usePreferences } from '../../../hooks/usePreferences';

function formatCurrency(cents: number, currency = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UsageAnalyticsTab(): React.ReactElement {
  const { data: analytics, isLoading, error } = useUsageAnalytics();
  const { data: prefs } = usePreferences();
  const currency = prefs?.currency ?? 'AUD';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <p className="text-red-800 dark:text-red-200">{error.message}</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-gray-600 dark:text-gray-400">Unable to load usage analytics</p>
      </div>
    );
  }

  const isEmpty = analytics.apiRequests === 0;

  return (
    <div className="space-y-6">
      {isEmpty ? (
        <div className="card">
          <div className="card-content p-8 py-12 text-center">
            <Activity className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
              No AI activity yet
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Use Smart Categorisation, Smart Insights, or other AI features to see usage here
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="card">
              <div className="card-content p-5">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium">This Month</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                  {formatCurrency(analytics.thisMonthCostCents, currency)}
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-content p-5">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm font-medium">API Requests</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                  {analytics.apiRequests}
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-content p-5">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm font-medium">Accepted Rate</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                  {analytics.acceptedRatePercent}%
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-content p-5">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm font-medium">Correct Rate</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                  {analytics.correctRatePercent}%
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-content p-5">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">Time Saved</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                  {analytics.timeSavedHours}h
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content p-6 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                AI Performance Summary
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {analytics.performanceSummary}
              </p>
            </div>
          </div>

          {analytics.recentActivity.length > 0 && (
            <div className="card">
              <div className="card-content p-6 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Recent AI Activity
                </h3>
                <ul className="space-y-3" role="list">
                  {analytics.recentActivity.map((item) => (
                    <li
                      key={item.id}
                      className="flex justify-between items-start py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {item.description}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {item.eventType}
                        </p>
                      </div>
                      <time
                        className="text-sm text-gray-500 dark:text-gray-400"
                        dateTime={item.timestamp}
                      >
                        {formatDate(item.timestamp)}
                      </time>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
